/**
 * @fileoverview
 * Rendering Modes section for the homepage.
 *
 * This is a client React component that renders the "Rendering Modes"
 * section with four mode cards: Exterior, Interior, Floorplan, Sketch.
 *
 * Content:
 * - Section subtitle: "Features"
 * - Section heading: "Rendering modes for every use case"
 * - Four mode cards with icon badges, title, description, and example image
 *
 * Design:
 * - Dark theme matching aideco.us
 * - Responsive card grid layout
 * - All visible copy is i18n-backed via Lingui (Trans / useLingui t)
 */

"use client";

import { useMemo } from "react";
import Image from "next/image";
import { Trans, useLingui } from "@lingui/react/macro";
import { SectionShell, SectionContainer, SectionEyebrow } from "../primitives";

interface RenderingMode {
  id: string;
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  metaItems: {
    icon: string;
    text: string;
  }[];
}

/**
 * RenderingModesSection renders the "Rendering Modes" section with
 * five mode cards featuring icon badges, titles, descriptions, and example images.
 *
 * Mobile: Uses sticky positioning for stacked card reveal effect (matches live site)
 * Desktop: Uses flex row layout with image on right
 *
 * @returns JSX.Element
 */
export function RenderingModesSection() {
  const { i18n, t } = useLingui();

  const renderingModes: RenderingMode[] = useMemo(
    () => [
      {
        id: "exterior",
        title: t`Exterior Mode`,
        description: t`Transform building facades and site plans into stunning photorealistic exterior visualizations.`,
        image: "/images/showcase/cliff-house.webp",
        imageAlt: t`Exterior rendering example showing a photorealistic building facade`,
        metaItems: [
          {
            icon: "/images/marketing/68638362ac6831a354ebe7ad_living.svg",
            text: t`4K Output`,
          },
          {
            icon: "/images/marketing/686389a382a840fdbfebbe05_dianing.svg",
            text: t`Buildings`,
          },
          {
            icon: "/images/marketing/686389af77440ad39869bc3a_coilor.svg",
            text: t`Facades`,
          },
        ],
      },
      {
        id: "interior",
        title: t`Interior Mode`,
        description: t`Generate detailed interior renders from floor plans showing furniture, materials, and lighting.`,
        image: "/images/showcase/minimalist-loft.webp",
        imageAlt: t`Interior rendering example showing a furnished room`,
        metaItems: [
          {
            icon: "/images/marketing/68638362ac6831a354ebe7ad_living.svg",
            text: t`HD Output`,
          },
          {
            icon: "/images/marketing/686389a382a840fdbfebbe05_dianing.svg",
            text: t`Rooms`,
          },
          {
            icon: "/images/marketing/686389af77440ad39869bc3a_coilor.svg",
            text: t`Furniture`,
          },
        ],
      },
      {
        id: "floorplan",
        title: t`Floorplan Mode`,
        description: t`Convert 2D floor plans into furnished 3D views with automatic room detection and styling.`,
        image: "/images/showcase/atrium-lobby.webp",
        imageAlt: t`Floorplan to 3D conversion example`,
        metaItems: [
          {
            icon: "/images/marketing/68638362ac6831a354ebe7ad_living.svg",
            text: t`2D to 3D`,
          },
          {
            icon: "/images/marketing/686389a382a840fdbfebbe05_dianing.svg",
            text: t`Plans`,
          },
          {
            icon: "/images/marketing/686389af77440ad39869bc3a_coilor.svg",
            text: t`Layouts`,
          },
        ],
      },
      {
        id: "sketch",
        title: t`Sketch Mode`,
        description: t`Transform your designs into elegant hand-drawn sketch style renders perfect for early presentations.`,
        image: "/images/marketing/686393e663f7f9b2f92e228b_rj6U5iUsVpr2EcDuHJdLpFeYe20.webp",
        imageAlt: t`Sketch style architectural render`,
        metaItems: [
          {
            icon: "/images/marketing/68638362ac6831a354ebe7ad_living.svg",
            text: t`Artistic`,
          },
          {
            icon: "/images/marketing/686389a382a840fdbfebbe05_dianing.svg",
            text: t`Pencil`,
          },
          {
            icon: "/images/marketing/686389af77440ad39869bc3a_coilor.svg",
            text: t`Concept`,
          },
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );
  const sectionAriaLabel =
    i18n.locale === "vi" ? "Phần các chế độ kết xuất" : "Rendering modes section";

  return (
    <SectionShell
      id="living"
      ariaLabel={sectionAriaLabel}
      noPadding
      className="overflow-visible py-6 sm:py-14 lg:py-20 xl:py-28"
    >
      {/* Section Title Wrapper - outside container like live site */}
      <SectionContainer className="flex flex-col items-center overflow-hidden xl:px-12">
        <div className="flex flex-col justify-center items-center gap-4 max-w-[800px] mb-8 sm:mb-12 lg:mb-16">
          <SectionEyebrow label={<Trans>Features</Trans>} />
          <h2 className="text-center max-w-[450px] text-xl font-bold leading-tight text-black sm:text-2xl md:text-3xl lg:text-[2.5rem] lg:leading-[1.2]">
            <Trans>Rendering modes for every use case</Trans>
          </h2>
        </div>
      </SectionContainer>

      {/* Main Content Container */}
      <SectionContainer className="xl:px-12">
        <div className="flex flex-col items-center w-full gap-20 overflow-visible">
          {/* 
            Cards layout:
            - Mobile: Fixed height section with sticky stacked cards (scroll-reveal effect)
            - Desktop: Flex row layout with image on right
          */}
          <div className="w-full h-[1280px] lg:h-auto lg:flex lg:flex-col lg:gap-10">
            {(() => {
              const zIndexClasses = ['z-[1]', 'z-[2]', 'z-[3]', 'z-[4]'];
              return renderingModes.map((mode, index) => (
              <div
                key={mode.id}
                className={`
                  overflow-hidden rounded-[10px] bg-white shadow-sm transition-shadow hover:shadow-md 
                  lg:flex lg:flex-row
                  sticky top-5
                  ${zIndexClasses[index]}
                  ${index < renderingModes.length - 1 ? 'mb-6 lg:mb-0' : ''}
                `}
              >
                {/* Card Content - Text on left for desktop */}
                <div className="flex flex-col justify-between bg-[#f5f5f5] rounded-[10px] p-5 lg:p-[50px] w-full lg:w-[40%]">
                  <div className="flex flex-col gap-4">
                    <h3 className="text-base font-semibold text-black sm:text-lg lg:text-xl">
                      {mode.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-gray-500 sm:mb-5">
                      {mode.description}
                    </p>
                  </div>

                  {/* Meta Items / Icon Badges */}
                  <div className="flex flex-col sm:flex-row gap-2.5 overflow-hidden mt-auto pt-4 sm:pt-0">
                    {mode.metaItems.map((item, itemIndex) => (
                      <div
                        key={itemIndex}
                        className="flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 sm:px-3 sm:py-1.5"
                      >
                        <Image
                          alt={`${item.text} icon`}
                          className="h-4 w-4"
                          height={16}
                          src={item.icon}
                          width={16}
                        />
                        <span className="text-base font-normal text-gray-500 leading-none">
                          {item.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Card Image Gallery - Right side for desktop, hidden on mobile for compactness */}
                <div className="hidden rounded-[10px] overflow-hidden lg:block lg:w-[60%] w-full">
                  <Image
                    alt={mode.imageAlt}
                    className="h-full w-full object-cover"
                    height={500}
                    loading="lazy"
                    quality={90}
                    sizes="60vw"
                    src={mode.image}
                    width={800}
                  />
                </div>
              </div>
            ));
            })()}
          </div>
        </div>
      </SectionContainer>
    </SectionShell>
  );
}
